import { useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';

interface HeadingStyle {
  size: string;
  weight: string;
  color: string;
  italic?: boolean;
  uppercase?: boolean;
}

interface HeadingsSettings {
  h1: HeadingStyle;
  h2: HeadingStyle;
  h3: HeadingStyle;
  h4: HeadingStyle;
  h5: HeadingStyle;
  h6: HeadingStyle;
}

interface ParagraphSettings {
  lineHeight: string;
  paragraphSpacing: string;
  fontFamily?: string;
  fontSize?: string;
}

interface ImageSettings {
  maxWidth: string;
  maxHeight: string;
}

/**
 * Bordi inferiori per H1/H2 — toggle globale + spessore/colore per livello.
 * Introdotto in v1.8.64.
 */
interface HeadingBordersSettings {
  enabled: boolean;
  h1: { width: string; color: string };
  h2: { width: string; color: string };
}

interface TypographySettings {
  headings: HeadingsSettings;
  paragraph: ParagraphSettings;
  images: ImageSettings;
  headingBorders: HeadingBordersSettings;
}

const defaultSettings: TypographySettings = {
  headings: {
    // Palette H1-H6 v1.8.74: scala "a V" sui blu istituzionali LNI.
    // H1 parte chiaro, si scurisce fino al picco di H3, poi torna a
    // schiarire fino ad H6 (che resta leggermente più scuro di H1).
    // H3 è in corsivo (oltre ad H5, già corsivo). H4 NON è più in corsivo.
    // Contrasti su bianco: H2/H3/H4 AAA, H5 AA, H1/H6 AA large.
    // v1.13.3: scala armonica (progressione geometrica) da H1=75% del valore
    // precedente (2.25rem→1.6875rem) fino a H6=90% del paragrafo (0.9rem).
    // Ratio ≈ 0.881 per livello. H6 ora UPPERCASE (vero maiuscolo, non small-caps).
    // v1.13.139: ridotte di ~10% le dimensioni di H4–H6 per migliorare
    // la distinzione gerarchica rispetto a H3 (H4 era troppo simile).
    h1: { size: '1.6875rem', weight: '800', color: '78, 138, 212' },                // 27px
    h2: { size: '1.4875rem', weight: '700', color: '40, 82, 158' },                 // ~23.8px
    h3: { size: '1.3125rem', weight: '700', color: '8, 28, 68', italic: true },     // 21px
    h4: { size: '1.0407rem', weight: '700', color: '28, 68, 138' },                 // ~16.65px (-10%)
    h5: { size: '0.9169rem', weight: '700', color: '55, 108, 182', italic: true },  // ~14.67px (-10%)
    h6: { size: '0.81rem',   weight: '700', color: '70, 128, 204', uppercase: true }, // 12.96px UPPERCASE (-10%)
  },
  paragraph: {
    lineHeight: '1.375',
    paragraphSpacing: '0.375rem',
    fontFamily: 'Trebuchet MS',
    fontSize: '1rem',
  },
  images: {
    maxWidth: '90%',
    maxHeight: '90vh',
  },
  // Bordi inferiori H1/H2: disattivati globalmente da v1.8.70.
  // In v1.8.71 confermata rimozione esplicita della sottolineatura H1.
  headingBorders: {
    enabled: false,
    h1: { width: '3px', color: '0, 48, 97' },
    h2: { width: '1px', color: '210, 220, 230' },
  },
};

/**
 * Merge difensivo: garantisce la presenza di tutte le chiavi attese
 * anche quando il DB non ha ancora la versione corrente dello schema.
 */
const mergeWithDefaults = (incoming: Partial<TypographySettings>): TypographySettings => ({
  headings: { ...defaultSettings.headings, ...(incoming.headings || {}) } as HeadingsSettings,
  paragraph: { ...defaultSettings.paragraph, ...(incoming.paragraph || {}) },
  images: { ...defaultSettings.images, ...(incoming.images || {}) },
  headingBorders: {
    ...defaultSettings.headingBorders,
    ...(incoming.headingBorders || {}),
    h1: { ...defaultSettings.headingBorders.h1, ...((incoming.headingBorders as any)?.h1 || {}) },
    h2: { ...defaultSettings.headingBorders.h2, ...((incoming.headingBorders as any)?.h2 || {}) },
  },
});

const generateCSS = (settings: TypographySettings): string => {
  const { headings, paragraph, images, headingBorders } = settings;
  
  const generateHeadingCSS = (level: keyof HeadingsSettings, selector: string) => {
    const h = headings[level];
    // Bordo inferiore solo per H1/H2 e solo se il toggle globale è attivo
    const borderRule = headingBorders.enabled && (level === 'h1' || level === 'h2')
      ? `border-bottom: ${headingBorders[level].width} solid rgb(${headingBorders[level].color}) !important;
         padding-bottom: 0.25rem !important;`
      : '';
    return `
${selector} {
  font-size: ${h.size} !important;
  font-weight: ${h.weight} !important;
  color: rgb(${h.color}) !important;
  ${h.italic ? 'font-style: italic !important;' : ''}
  ${h.uppercase ? 'text-transform: uppercase !important;' : ''}
  margin-top: 0.9rem !important;
  /* v1.13.3: spazio sotto il titolo azzerato per qualunque livello H */
  margin-bottom: 0 !important;
  ${borderRule}
}`;
  };

  return `
/* Dynamic Typography Styles - Generated from Database */

/* Headings for .content-styled */
${generateHeadingCSS('h1', '.content-styled h1')}
${generateHeadingCSS('h2', '.content-styled h2')}
${generateHeadingCSS('h3', '.content-styled h3')}
${generateHeadingCSS('h4', '.content-styled h4')}
${generateHeadingCSS('h5', '.content-styled h5')}
${generateHeadingCSS('h6', '.content-styled h6')}

/* Headings for .ProseMirror (TipTap editor) */
${generateHeadingCSS('h1', '.ProseMirror h1')}
${generateHeadingCSS('h2', '.ProseMirror h2')}
${generateHeadingCSS('h3', '.ProseMirror h3')}
${generateHeadingCSS('h4', '.ProseMirror h4')}
${generateHeadingCSS('h5', '.ProseMirror h5')}
${generateHeadingCSS('h6', '.ProseMirror h6')}

/* Paragraph settings */
.content-styled,
.ProseMirror {
  font-family: ${paragraph.fontFamily || 'Trebuchet MS'}, 'Outfit', sans-serif !important;
  font-size: ${paragraph.fontSize || '1rem'} !important;
}

.content-styled p,
.content-styled li,
.ProseMirror p,
.ProseMirror li {
  line-height: ${paragraph.lineHeight} !important;
}

.content-styled p + p,
.content-styled p + h1, .content-styled p + h2, .content-styled p + h3,
.content-styled p + h4, .content-styled p + h5, .content-styled p + h6,
.ProseMirror p + p,
.ProseMirror p + h1, .ProseMirror p + h2, .ProseMirror p + h3,
.ProseMirror p + h4, .ProseMirror p + h5, .ProseMirror p + h6 {
  margin-top: ${paragraph.paragraphSpacing} !important;
}

/* Image settings */
.content-styled img,
.ProseMirror img {
  max-width: ${images.maxWidth} !important;
  max-height: ${images.maxHeight} !important;
}
`;
};

export const DynamicTypographyStyles = () => {
  const [css, setCss] = useState<string>('');

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const { data, error } = await supabase
          .from('typography_settings')
          .select('setting_key, setting_value');

        if (error) {
          console.error('Error fetching typography settings:', error);
          setCss(generateCSS(defaultSettings));
          return;
        }

        if (data && data.length > 0) {
          const incoming: Partial<TypographySettings> = {};
          data.forEach((row) => {
            const key = row.setting_key as keyof TypographySettings;
            (incoming as any)[key] = row.setting_value;
          });
          setCss(generateCSS(mergeWithDefaults(incoming)));
        } else {
          setCss(generateCSS(defaultSettings));
        }
      } catch (error) {
        console.error('Error fetching typography settings:', error);
        setCss(generateCSS(defaultSettings));
      }
    };

    fetchSettings();

    // Subscribe to realtime changes
    const channel = supabase
      .channel('typography-changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'typography_settings',
        },
        () => {
          fetchSettings();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, []);

  if (!css) return null;

  return <style dangerouslySetInnerHTML={{ __html: css }} />;
};
